import { getPsetsAndQsets, getType, type PsetGroup } from '@viewer/lib/ifcItemData'

/** Os pedidos ao motor para ler as propriedades de um elemento e as do
 *  seu type, e a junção das duas.
 *
 *  Uma instância herda as propriedades do type: o que está no
 *  IfcWallType vale para todas as paredes desse type, e a instância só
 *  repete o que muda. Quem lista propriedades (a Tabela, a comparação
 *  de vários elementos) tem de mostrar as duas, com a da instância a
 *  valer quando coincidem.
 *
 *  Os pedidos aprofundam só a relação que leva aos valores. Pedir
 *  `relations: true` em tudo trazia o modelo inteiro: com duas paredes,
 *  o piso, todos os elementos do piso e as relações deles (7785
 *  entidades em vez de 35 num modelo de 25 MB). */

/** Elemento com os seus Psets e Qtos (IsDefinedBy -> HasProperties /
 *  Quantities) e a ligação ao type (IsTypedBy, sem ir mais fundo). */
export const PEDIDO_DO_ELEMENTO = {
  attributesDefault: true,
  relationsDefault: { attributes: true, relations: false },
  relations: { IsDefinedBy: { attributes: true, relations: true } },
}

/** Type com os seus Psets (HasPropertySets -> HasProperties). */
export const PEDIDO_DO_TYPE = {
  attributesDefault: true,
  relationsDefault: { attributes: true, relations: false },
  relations: { HasPropertySets: { attributes: true, relations: true } },
}

export interface ConjuntosDoElemento {
  psets: PsetGroup[]
  qsets: PsetGroup[]
}

interface ModeloLegivel {
  getItemsData(ids: number[], config: typeof PEDIDO_DO_TYPE): Promise<unknown[]> | unknown[]
}

function localIdDe(o: unknown): number | null {
  const l = (o as { _localId?: unknown } | null)?._localId
  if (typeof l === 'number') return l
  if (l && typeof l === 'object' && typeof (l as { value?: unknown }).value === 'number') {
    return (l as { value: number }).value
  }
  return null
}

/** Lê vários elementos de uma vez, sem cair na cache do motor.
 *
 *  Dentro de um getItemsData o motor guarda cada entidade na primeira
 *  forma em que a lê: se um piso e as suas paredes vêm no mesmo pedido,
 *  a parede é lida primeiro como relação do piso (rasa, sem os Psets) e
 *  depois devolvida assim no lugar dela. Os pedidos que aparecem como
 *  relação de outro do mesmo lote lêem-se outra vez, sozinhos. */
export async function lerElementos(
  modelo: { getItemsData(ids: number[], config: typeof PEDIDO_DO_ELEMENTO): Promise<unknown[]> | unknown[] },
  ids: number[],
  pedido: typeof PEDIDO_DO_ELEMENTO = PEDIDO_DO_ELEMENTO,
): Promise<unknown[]> {
  if (ids.length === 0) return []
  const out = [...(await modelo.getItemsData(ids, pedido))]
  const pedidos = new Set(ids)
  const aninhados = new Set<number>()
  const vistos = new Set<unknown>()
  const andar = (o: unknown, topo: boolean) => {
    if (!o || typeof o !== 'object' || vistos.has(o)) return
    vistos.add(o)
    if (!topo) {
      const id = localIdDe(o)
      if (id !== null && pedidos.has(id)) aninhados.add(id)
    }
    for (const v of Object.values(o)) {
      if (Array.isArray(v)) for (const x of v) andar(x, false)
      else if (v && typeof v === 'object') andar(v, false)
    }
  }
  for (const r of out) andar(r, true)
  for (const id of aninhados) {
    const i = ids.indexOf(id)
    const [sozinho] = await modelo.getItemsData([id], pedido)
    out[i] = sozinho
  }
  return out
}

/** O id do type do elemento, se tiver. */
export function idDoType(item: Record<string, unknown>): number | null {
  return getType(item)?.localId ?? null
}

/** Lê os Psets de vários types de uma vez. */
export async function lerConjuntosDosTypes(
  modelo: ModeloLegivel,
  ids: number[],
): Promise<Map<number, ConjuntosDoElemento>> {
  const out = new Map<number, ConjuntosDoElemento>()
  const unicos = [...new Set(ids)]
  // Lista vazia no motor quer dizer "todos": nunca se pede assim.
  if (unicos.length === 0) return out
  const dados = await modelo.getItemsData(unicos, PEDIDO_DO_TYPE)
  dados.forEach((d, i) => {
    const t = (d ?? {}) as Record<string, unknown>
    out.set(unicos[i], getPsetsAndQsets({ IsDefinedBy: t.HasPropertySets }))
  })
  return out
}

function juntarGrupos(daInstancia: PsetGroup[], doType: PsetGroup[]): PsetGroup[] {
  const out = daInstancia.map((g) => ({ ...g, rows: [...g.rows] }))
  for (const t of doType) {
    const marcadas = t.rows.map((r) => ({ ...r, origem: 'type' as const }))
    const mesmo = out.find((g) => g.name === t.name)
    if (!mesmo) {
      out.push({ ...t, rows: marcadas })
      continue
    }
    // Onde coincidem, vale a instância; o resto do type acrescenta-se.
    const chaves = new Set(mesmo.rows.map((r) => r.key))
    for (const r of marcadas) if (!chaves.has(r.key)) mesmo.rows.push(r)
  }
  return out
}

/** Os conjuntos da instância com os do type acrescentados, marcados com
 *  `origem: 'type'`. */
export function juntarComType(
  daInstancia: ConjuntosDoElemento,
  doType: ConjuntosDoElemento | undefined,
): ConjuntosDoElemento {
  if (!doType) return daInstancia
  return {
    psets: juntarGrupos(daInstancia.psets, doType.psets),
    qsets: juntarGrupos(daInstancia.qsets, doType.qsets),
  }
}
